/**
 * Card tint tokens — the ONE definition (TV1-2291 · SDD §4, ticket 13).
 *
 * ⚠️ A TOKEN SET, NOT A COLOUR PICKER. Seven names — `none`, `teal`, `amber`,
 * `green`, `red`, `navy`, `slate` — and each resolves to a DIFFERENT value per
 * theme, through `light-dark()` in `WidgetCard.module.css`.
 *
 * That per-theme resolution is the whole reason these are tokens. A stored hex
 * cannot do it: the pastel that reads correctly over dark ink in light mode
 * becomes a LIT BLOCK behind light ink in dark mode, and the contrast floor
 * breaks silently — nothing throws, nothing looks wrong to the author who picked
 * it in light mode, and the report is shared. Reports are shared documents, so
 * the person who suffers the unreadable card is rarely the person who chose it.
 *
 * ▸ **If free hex is ever genuinely wanted, it needs a light/dark PAIR per value.**
 *   A single `#rrggbb` on the wire is not a widening of this set, it is a
 *   different (and worse) feature: `{ light: '#…', dark: '#…' }` — authored,
 *   validated and contrast-checked as a pair — is the only shape that preserves
 *   what the tokens buy. Do not "just allow a hex" and let `light-dark()` fall
 *   back to the same value on both sides.
 *
 * WHERE THE VALUES CAME FROM. `docs/prs/TV1-2291/prototypes/prototype-b-grid.html`
 * (`--bg-*` / `--bd-*`), browser-verified in both themes. Ported through Mantine
 * variables wherever this app's theme already owns the exact value — and it owns
 * most of them, because the prototype was drawn from `styles/theme.ts` in the
 * first place:
 *
 * | token | light                  | dark              |
 * |-------|------------------------|-------------------|
 * | none  | `white` / `gray-2`     | `dark-6` / `dark-4`  |
 * | amber | `warning-0` / `-2`     | prototype hex     |
 * | green | `success-0` / `-2`     | prototype hex     |
 * | red   | `danger-0`  / `-2`     | prototype hex     |
 * | navy  | `secondary-0` / `-2`   | prototype hex     |
 * | slate | `gray-1` / `gray-3`    | `dark-5` / `dark-3`  |
 * | teal  | `#ebf2f4` (brand)      | prototype hex     |
 *
 * The remaining literals are the ones with NO palette slot: `#ebf2f4` is the
 * brand accent that `.claude/rules/design-system.md` itself writes as a hex, and
 * the five muted dark tints (`#263a3e`, `#3a3227`, …) are deliberately
 * desaturated surfaces that no Mantine shade approximates — the saturated `-9`
 * shades (`teal-9` `#087f5b`, `yellow-9` `#e67700`) are exactly the "lit block"
 * failure described above. They are still centralised, still named, and still
 * theme-paired: the tint classes ARE the custom properties.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE THE TINT LIVES ON THE DATA: the layout's widget WRAPPER, beside
 * `x`/`y`/`width`/`height` — NOT on the widget DEFINITION (ticket 20, settled).
 *
 * `widgetGrammar.modelVariant()` derives each model-facing schema by omitting
 * exactly `id` and `widgetComponentProps` from the domain definition, so ANY
 * other top-level definition field is inherited into the schema handed to the
 * LLM. A per-definition `tint` would hand a language model a card-presentation
 * key it must never author, and widen the ADR-051 boundary with nothing to catch
 * it. Read `w.tint`; never `w.definition.tint`.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Owned here, in `WidgetCard/`, rather than in `WidgetInspector/` where ticket 16
 * first put it: the CARD is the thing that has to paint a tint for a READER, and
 * the inspector is one optional editor affordance on top. `WidgetInspector`
 * re-exports these names so there is a single definition, and its swatches
 * consume `WIDGET_TINT_CLASS` directly rather than keeping a second value table
 * in `WidgetInspector.module.css` that has to be hand-synchronised.
 */

import type { Widget, WidgetType } from '../Widgets/types';
import { WIDGET_CATALOG } from '../widgetCatalog';
import {
  isCustomTint, isTintValue, isWidgetTint, WIDGET_TINT_TOKENS, type WidgetTint
} from './tintTokens';
import { deriveTintPair, type TintSurfaces } from './tintColor';
import { parseColorSteps, resolveStepColor } from '../Widgets/WebWidget/colorSteps';
import classes from './WidgetCard.module.css';

/**
 * The seven tokens, the `WidgetTint` type and the `isWidgetTint` guard live in
 * `./tintTokens` and are re-exported here, so this file stays the single import
 * site every renderer and `WidgetInspector` already use.
 *
 * ⚠️ It is ONE definition, still — the split is not a second copy. The reason it
 * had to move is mechanical and is documented in `tintTokens.ts`: this file
 * imports `WidgetCard.module.css`, which the ROOT jest runner cannot load (no CSS
 * `moduleNameMapper`), and `widgetDefinitionAdapter` — which needs the token set
 * to guard the save path (t34) — is imported by that runner's ajv oracle. The
 * class table below is the part that genuinely needs the stylesheet; the names do
 * not.
 */
export { isCustomTint, isTintValue, isWidgetTint, WIDGET_TINT_TOKENS };
export type { WidgetTint };

/**
 * Widget types that accept a tint.
 *
 * **Charts and tables refuse one** (SDD §4): a background tint fights the series
 * palette on a chart and the row banding on a table. `criticalStock` is tabular
 * too and is excluded for the same reason.
 *
 * ⚠️ **`spacer` is deliberately EXCLUDED — this was the open call on ticket 13.**
 * The prototype marks it tintable; it should not be, for three reasons:
 *
 *  1. **A spacer's job is to be invisible.** It exists because vertical
 *     compaction (ticket 8) closes every gap, so a deliberate one is otherwise
 *     unexpressible — that is the whole of its load-bearing role (ticket 21).
 *     Tinting it makes the gap visibly not a gap, which is the exact defect
 *     `CHROMELESS_WIDGET_TYPES` already suppresses its title to avoid.
 *  2. **It is unlabelled by construction.** `spacer` is chromeless, so a tinted
 *     spacer is a coloured rectangle with no title, no data and no caption. In a
 *     SHARED report a reader cannot tell it apart from a widget that failed to
 *     render — an empty coloured box is what breakage looks like.
 *  3. **It would smuggle the colour picker back in.** `spacer`'s
 *     `MIN_CONTENT_PX` is 0, so it can be any size down to a single cell. A
 *     tintable spacer is therefore a free-form paint primitive — arbitrary
 *     coloured rectangles at arbitrary positions — which is precisely the
 *     "colour picker" this token set exists NOT to be (SDD K8).
 *
 * If a deliberate coloured band is wanted later, the honest answer is a tinted
 * `heading` (it carries a label, so a reader knows what the band is) or a
 * dedicated `band`/`section` type — not a spacer that quietly stops being a gap.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️ **THE OPEN CALL ON TICKET 25: which of the four readmodel types join.**
 * Answer: **`count` does; `alert`, `list` and `progress` do not.** The rule the
 * exclusions above encode is *"a tint must not fight a colour that is already
 * carrying meaning"*, and it settles all four:
 *
 *  - **`count` — IN.** It is a number tile with no colour semantics of its own:
 *    literally `kpi` with a readmodel behind it instead of a Cube measure, same
 *    box model, same type scale, same `MIN_CONTENT_PX` (140). The two sit side by
 *    side in the headline row, so making one tintable and the other not would be
 *    an arbitrary difference a reader experiences as a bug.
 *  - **`alert` — OUT, and this is the interesting one.** Its `severity` IS a
 *    colour, it is REQUIRED by `UiWidgetAlertSchema`, and it is the one colour in
 *    this app a seller reads as a *judgement about their business*. A background
 *    tint would be a second, author-chosen colour on the same card, with nothing
 *    stopping the two contradicting each other — a `high` alert painted `green` in
 *    a SHARED report, which is worse than no colour at all. Same rule as the
 *    charts, different palette being fought.
 *  - **`list` — OUT.** Tabular: a tint fights the row separators exactly as it
 *    fights `table`'s row banding.
 *  - **`progress` — OUT.** The reading IS the contrast between the bar's fill and
 *    its track; a tint behind them compresses that contrast, and the widget's
 *    entire information content is that one ratio.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Kept as data next to the tokens rather than as a condition in the card and a
 * second one in the inspector: the two drifting apart is how a tint ends up
 * offered in the panel and dropped by the renderer, or worse, the reverse.
 */
/*
 * ⚠️ WIDENED 2026-08-17, on the ticket owner's call ("i want to be able to have a
 * colour picker on the widgets i can choose color. and be able to choose colour
 * also on the indicators").
 *
 * It was `kpi | text | count`. The reasoning that excluded the rest — *a tint must
 * not fight a colour that is already carrying meaning* — is still the right rule,
 * but it was applied far too broadly. "The series palette exists" is not the same
 * claim as "this colour IS the data": a chart's series colours sit INSIDE the plot,
 * and a wash behind them no more competes with them than the page does. Refusing
 * every chart, every table and every list on that basis left an author who wanted
 * to group three cards visually with no way to do it, and a panel that said "no"
 * without offering an alternative.
 *
 * So the exclusions are now only the cases where the card's colour IS its content:
 *
 *  - ~~**`alert`**~~ — **REMOVED FROM THIS LIST 2026-08-18.** The argument was that
 *    `severity` is the one colour a seller reads as a judgement, so a second
 *    author-chosen colour could contradict it (a `high` alert painted green, in a
 *    SHARED report). The ticket owner asked for the opposite — item 5: alerts follow
 *    the Dashboard's fixed tiles, *"colours should be pickable"* — and the resolution
 *    that keeps both is a DEFAULT rather than a refusal: an untinted alert is now
 *    painted from its severity automatically (`resolveAlertSeverityTint`), which is
 *    exactly the Dashboard's behaviour, and an author who sets a tint replaces it.
 *    The contradiction the old rule feared is still possible, and is now the
 *    author's to make deliberately — which is the same trust every other card
 *    already gets, and the alternative was an inspector that said "no" on the one
 *    type the review specifically asked to be colourable.
 *  - **`progress`** — the reading IS the contrast between the bar's fill and its
 *    track, and the widget's entire information content is that one ratio.
 *  - **`spacer`** — invisible by construction (its job is to BE the gap), unlabelled,
 *    and sizeable down to a single cell; a tintable spacer is a free-form paint
 *    primitive, and a coloured box with no title is what breakage looks like to a
 *    reader. Unchanged from t13, and the reasoning there still holds in full.
 *
 * Everything else may take a tint. Derived by subtraction rather than listed, so a
 * NEW widget type is tintable by default and only has to be named here if its
 * colour genuinely carries meaning — the opposite of the old list, which silently
 * refused every type nobody remembered to add.
 */
const UNTINTABLE_WIDGET_TYPES: ReadonlySet<WidgetType> =
  new Set<WidgetType>([ 'progress', 'spacer' ]);

export const TINTABLE_WIDGET_TYPES: ReadonlySet<WidgetType> = new Set<WidgetType>(
  ( Object.keys( WIDGET_CATALOG ) as WidgetType[]).filter(( type ) => !UNTINTABLE_WIDGET_TYPES.has( type ))
);

/** `true` when the widget type accepts a background tint. */
export const isTintableWidgetType = ( type: WidgetType | string | undefined ): boolean =>
  TINTABLE_WIDGET_TYPES.has( type as WidgetType );

/**
 * Token -> CSS-module class. The class sets `--widget-tint-bg` /
 * `--widget-tint-border` and paints nothing itself, so the two consumers — the
 * card surface and the inspector's swatches — share ONE value table while each
 * decides how to paint it.
 */
export const WIDGET_TINT_CLASS: Record<WidgetTint, string> = {
  none: classes.tintNone,
  teal: classes.tintTeal,
  amber: classes.tintAmber,
  green: classes.tintGreen,
  red: classes.tintRed,
  navy: classes.tintNavy,
  slate: classes.tintSlate
};

/**
 * The EFFECTIVE tint of a placed widget, or `undefined` for "no tint".
 *
 * Three ways to get `undefined`, and all three must render identically to a card
 * that was never tinted:
 *
 *  - the wrapper carries no `tint` at all (every layout persisted before t20);
 *  - it carries `'none'` — the token that MEANS untinted. It is not painted as
 *    "white / dark-6" even though that is what its swatch shows, because then
 *    "no tint" and `'none'` would be two subtly different renderings of the same
 *    intent. The swatch shows the surface colour precisely because choosing
 *    `none` returns the card TO the surface;
 *  - the widget's type does not accept one. A chart or a table can legitimately
 *    carry a persisted `tint`: the domain schema deliberately does NOT encode
 *    the refusal (it lives on the wrapper, uniform across all sixteen `anyOf`
 *    members), so the refusal is a UI rule and this is where it is enforced for
 *    the RENDERER. The inspector explaining that a chart takes no tint is not
 *    enough — the inspector is not in the reader's path.
 *
 * Reads `widget.tint`. The definition is never consulted; see the module header.
 */
export const resolveWidgetTint = (
  widget: Pick<Widget, 'tint' | 'definition'> | undefined
): WidgetTint | undefined => {
  const tint = widget?.tint;
  if ( !isWidgetTint( tint ) || tint === 'none' ) return undefined;
  return isTintableWidgetType( widget?.definition?.type ) ? tint : undefined;
};

/**
 * The CUSTOM half of the same question (2026-08-17): the author-picked colour, as
 * the six CSS values a card needs, or `undefined`.
 *
 * A separate function from {@link resolveWidgetTint} rather than a wider return
 * type, because the two produce different KINDS of thing — a class name and a set
 * of inline custom properties — and the card applies them differently. Both drop
 * the tint for a type that refuses one, and they must: the refusal is a UI rule
 * that the schema deliberately does not encode, so the renderer is where it is
 * enforced for a READER, who has no inspector to explain it.
 *
 * `isCustomTint` re-validates the hex here even though the domain schema constrains
 * it — a persisted layout can predate any schema, and this value goes into the DOM.
 */
export const resolveCustomWidgetTint = (
  widget: Pick<Widget, 'tint' | 'definition'> | undefined
): TintSurfaces | undefined => {
  const tint = widget?.tint;
  if ( !isCustomTint( tint )) return undefined;
  if ( !isTintableWidgetType( widget?.definition?.type )) return undefined;
  return deriveTintPair( tint );
};

/**
 * The author's own TEXT colour, if they set one (2026-08-18).
 *
 * Deliberately NOT gated on `isTintableWidgetType`, unlike the two tint resolvers
 * above. A chart and a table refuse a BACKGROUND tint because a tint fights the
 * series palette on one and row banding on the other — neither of those arguments
 * is about ink. A darker label colour on a table is a perfectly ordinary choice,
 * and refusing it would be cargo-culting the tint rule into a place it never
 * applied.
 *
 * `isCustomTint` re-validates here even though the domain schema constrains the
 * field, for the same reason it does for a tint: a persisted layout can predate any
 * schema version, and this value reaches a CSS custom property in the DOM.
 */
export const resolveWidgetTextColor = (
  widget: Pick<Widget, 'textColor'> | undefined
): string | undefined => {
  const color = widget?.textColor;
  return isCustomTint( color ) ? color : undefined;
};

/**
 * The alert tile's severity, as a CARD SURFACE class (2026-08-18).
 *
 * The Dashboard's fixed alert tiles tint the whole `Paper` by `alertLevel`, and item
 * 5 of the review asks the alert WIDGET to look like them. The card is the only
 * element that can paint that — a panel drawn inside the body would read as a second
 * frame inside the first — so the severity is read here and turned into one of three
 * classes in `WidgetCard.module.css`.
 *
 * ⚠️ THE AUTHOR STILL WINS. An explicit `tint` — token or custom colour — makes this
 * stand down entirely, so the severity surface is a DEFAULT rather than an override.
 * Without that, dropping an alert onto a report would take away the colour control
 * every other card has, and the inspector would offer a choice that did nothing.
 *
 * Reads `severity` off `widgetComponentProps`, which is where `UiWidgetAlertSchema`
 * declares it — never off the definition root. A top-level read would be inherited
 * into the model-facing schema by `widgetGrammar.modelVariant()` the day `alert`
 * joins `MODEL_WIDGET_VARIANTS`; the same reasoning as `href` in `WebWidget`.
 */
export const resolveAlertSeverityTint = (
  widget: Pick<Widget, 'tint' | 'definition'> | undefined
): string | undefined => {
  if ( widget?.definition?.type !== 'alert' ) return undefined;
  // Any author-set tint — including `'none'`, which is an explicit "leave it plain"
  // and must not be overridden by a colour the author did not ask for.
  if ( widget.tint !== undefined ) return undefined;

  const severity = ( widget.definition as { widgetComponentProps?: { severity?: unknown }})
    ?.widgetComponentProps?.severity;

  if ( severity === 'low' ) return classes.alertSeverityLow;
  if ( severity === 'high' ) return classes.alertSeverityHigh;
  // `'medium'` is also the renderer's fallback for an absent or unrecognised value
  // (`AlertWidget`), so the surface and the ink agree on what an untreated alert is.
  return classes.alertSeverityMedium;
};

/**
 * A matching colour STEP, as a full card surface (2026-08-19).
 *
 * ⚠️ THIS CHANGES WHAT A STEP MEANS, on the ticket owner's correction: *"the tramos
 * i told you for colours on alerts, are for background and text, not text only."*
 *
 * A step used to recolour the NUMBER and the badge only, leaving the card wearing
 * its severity surface — so a tile whose count crossed into the red band still sat
 * on an amber card, and the two disagreed about how bad things were. The band now
 * paints the whole card, which is also how the Dashboard's fixed alert tiles work:
 * the severity is the SURFACE, not an accent on it.
 *
 * ## "Background and text", with the text DERIVED
 *
 * The author picks one colour per band and gets both, because the second is not a
 * free choice — it is a consequence. `deriveTintPair` composites the picked colour
 * over each theme's page and reads the result's luminance to choose between the
 * app's near-black and near-white ink, so a band is readable whatever colour and
 * whatever alpha the author gave it. Asking for two colours per band would be four
 * inputs for a three-band tile, and three of them would be wrong.
 *
 * ## Why it takes the DATA
 *
 * A step is matched against the tile's VALUE, which the card does not have — the
 * card is handed a widget, and the value lives in the fetched payload. So the
 * renderers (viewer and editor) resolve this and pass the result down; they are the
 * only place both halves are in scope. That is also why this is a function here
 * rather than a branch inside `WidgetCard`.
 *
 * ⚠️ AN AUTHOR-SET TINT STILL WINS, exactly as it does over the severity surface.
 * Steps are a smarter DEFAULT, not an override: an author who picked a colour for
 * the card meant that colour.
 */
export const resolveWidgetStepTint = (
  widget: Pick<Widget, 'tint' | 'definition'> | undefined,
  data: unknown
): TintSurfaces | undefined => {
  if ( widget?.definition?.type !== 'alert' ) return undefined;
  // Any explicit tint — including `'none'`, which is a deliberate "leave it plain".
  if ( widget.tint !== undefined ) return undefined;

  const steps = parseColorSteps(
    ( widget.definition as { widgetComponentProps?: { colorSteps?: unknown }})?.widgetComponentProps?.colorSteps
  );
  if ( !steps ) return undefined;

  const color = resolveStepColor( readWidgetCount( data ), steps );
  return color ? deriveTintPair( color ) : undefined;
};

/**
 * The count inside a widget's fetched payload, or `null`.
 *
 * Deliberately tolerant and deliberately SMALL. It is not a second copy of
 * `resolveReadmodelWidgetState` — that function classifies four outcomes and is the
 * renderer's job; this one answers a single question ("is there a number here?") for
 * a purely presentational decision, and every failure mode collapses to `null`,
 * which `resolveStepColor` already treats as "no band" (an unmeasured tile is not in
 * the lowest band — it is unknown, and colouring it would be a fabricated reading).
 */
const readWidgetCount = ( data: unknown ): number | null => {
  if ( typeof data !== 'object' || data === null ) return null;
  const state = data as { status?: string; data?: unknown };
  const payload = state.status === 'ok' ? state.data : data;
  if ( typeof payload !== 'object' || payload === null ) return null;
  const count = ( payload as { count?: unknown }).count;
  return typeof count === 'number' && Number.isFinite( count ) ? count : null;
};
